import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import URL from '../../../constants/url';
import axiosApi from '../../../api/daonAxios';
import style from './ContactlessMain.module.css';

// 탭 UI
import { Tab } from '@mui/base/Tab';
import { TabsList } from '@mui/base/TabsList';
import { TabPanel } from '@mui/base/TabPanel';
import { Tabs } from '@mui/base/Tabs';
import { formatDate, getAgeByBirthday, getCalculateDDay } from '../../../assets/js/comm';
import ToastPopup from '../../../components/comm/ToastPopup';
import CODE from '../../../constants/code';

//스켈레톤
import Skeleton from '@mui/material/Skeleton';
import noDataImg from '../../../images/common/i_3d_no_data_doc.svg';

/**
 * 기기 타임존 무관, 한국시간(KST) 진료 시작 15분 전 도달 여부 체크 (UTC 절대값 비교)
 * @param {string} bgngDtStr - "YYYYMMDDHHmmss" 형식의 KST 기준 시간
 * @returns {boolean} 입장 가능 여부
 */
const isJoinAvailableKST = (bgngDtStr) => {
    if (!bgngDtStr || bgngDtStr.length < 14) return false;

    const year = bgngDtStr.substring(0, 4);
    const month = bgngDtStr.substring(4, 6) - 1;
    const day = bgngDtStr.substring(6, 8);
    const hour = bgngDtStr.substring(8, 10);
    const min = bgngDtStr.substring(10, 12);

    // KST(UTC+9)를 절대적인 UTC 밀리초로 변환 (9시간 차감)
    const targetUtcTime = new Date(Date.UTC(year, month, day, hour - 9, min)).getTime();

    // 진료 15분 전 시간
    const availableUtcTime = targetUtcTime - (15 * 60 * 1000);

    // 현재 UTC 시간과 비교 (로컬 타임존 무시)
    return Date.now() >= availableUtcTime;
};

/**
 * 해외비대면진료 메인
 * @returns {JSX.Element}
 * @constructor
 */
const ContactlessMain = () => {

    const userStr = sessionStorage.getItem("loginUser");
    const user = userStr ? JSON.parse(userStr) : null;

    const navigate = useNavigate();
    //console.log("=========> user : ", user);

    // 요청사용자 정보
    const [reqPatnt, setReqPatnt] = useState({
        rsvctmPatntNo: user !== null ? user?.usrHsptlMap[0]?.cnncPatntNo : "",
        //rsvctmId: user !== null ? user?.usrInfoId : "", -- 실증에선 상관없으므로.. 풀어둠.
        chrgHsptlCd: user !== null ? user?.usrHsptlMap[0]?.cnncHsptlCd : "",
    });

    // 비대면진료 조회
    const [telemedicineList, setTelemedicineList] = useState([]);

    // 탭액션 값
    const [tabValue, setTabValue] = useState("before");
    // 토스트메시지 관련 세트
    const [toast, setToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    // 화면특수기능렌더 useRef 선언
    const isInitialRender = useRef(true);

    //로딩 상태 값 추가
    const [loading, setLoading] = useState(false);

    // 실시간 버튼 활성화(15분 전) 오차 방지를 위한 1초 주기 렌더링 트리거
    const [tick, setTick] = useState(Date.now());

    /**
     * 기능부
     * @param event
     * @param newValue
     */
    const handleOnTabChange = (event, newValue) => {
        console.log(" ============> handleOnTabChange newValue : ", newValue);
        setTabValue(newValue);
        searchTelemedicineScheduleList(newValue);
    }

    /**
     * 해외비대면진료 일정 리스트 조회 API 호출
     * @param gbnVal
     */
    const searchTelemedicineScheduleList = (gbnVal = tabValue) => {
        setLoading(true);   // 로딩 시작

        const requestOverseasUrl = "/overseas/retrieveTelemedicineScheduleList";

        const jToken = localStorage.getItem("jToken");

        const requestOption = {
            headers: {
                //"Content-Type": "application/json",
                "Authorization": `Bearer ${jToken}`,
            },
        }

        const requestBody = {
            ...reqPatnt
            , gbnVal: gbnVal
        }

        axiosApi.post(requestOverseasUrl, requestBody, requestOption)
            .then((res) => {
                const resultData = res.data;
                // console.log("=========> searchTelemedicineScheduleList resultData : ", resultData);
                if (resultData.resultCode === CODE.RCV_SUCCESS) {
                    // 사이즈체크는 기본 백단에서 해주고 넘기니 믿고 셋 처리
                    setTelemedicineList(resultData.result.telemedicineScheduleList);
                } else {
                    setTelemedicineList([]);
                    // setToastMessage("No results were found.");
                    // setToast(true);
                }

            })
            .catch((err) => {
                console.log("=========> err : ", err);
            })
            .finally(() => {
                setLoading(false);  // 로딩 종료
            });;
    };

    useEffect(() => {
        if (isInitialRender.current) {
            searchTelemedicineScheduleList();
            isInitialRender.current = false;
        }

        if (tabValue === "before") { // before 인 경우, 편의를 위해 30초 폴링처리
            const intervalId = setInterval(() => {
                searchTelemedicineScheduleList();
            }, 30000);
            return () => clearInterval(intervalId);
        }
    }, [tabValue]);

    useEffect(() => {
        // 1초마다 강제로 화면을 렌더링시켜서 Date.now()를 최신화함
        const timer = setInterval(() => {
            setTick(Date.now());
        }, 1000);
        return () => clearInterval(timer);
    }, []);

    /**
     * 비대면상담진료실 입장
     * @param joinUrl
     */
    const handleOnJoinMeeting = (joinUrl) => {
        if (window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage(JSON.stringify({
                type: 'OPEN_EXTERNAL_URL',
                url: joinUrl
            }));
        } else {
            window.open(joinUrl, '_blank', 'noopener,noreferrer');
        }
    }

    //스켈레톤 로딩
    const ConsultationSkeleton = () => (
        <>
            {[1, 2, 3].map((_, idx) => (
                <div className={style['infrom-box']} >
                    <div className={style['top']}>
                        <div className={style['left']}>
                            {/* <Skeleton variant="text" width="100px" height={30} /> */}
                            <div>
                                <Skeleton variant="text" width="200px" height={25} />
                            </div>

                            <div className={style['txt-box']}>
                                <Skeleton variant="text" width="180px" height={20} />
                                <Skeleton variant="text" width="120px" height={20} />
                                <Skeleton variant="text" width="100px" height={20} />
                            </div>
                        </div>
                        {/* <div className={style['right']}>
                            <Skeleton variant="circular" width="44px" height={44} style={{ backgroundColor: "#D6E6FF"}} />

                        </div> */}
                    </div>
                    {/* <div className={style['bottom']}>
                        <Skeleton variant="rectangular" width="100%" height={40} style={{ borderRadius: '8px', backgroundColor: "#3284FF"}} />
                    </div> */}
                </div>
            ))}
        </>
    );


    return (
        <>

            <div id="contactless-wrap" className='inner-wrap' data-pad="normal">


                <div className={style['head']}>
                    <div className={style['left']}>
                        <strong>
                            Telemedicine Consultation<br />
                            for International Patients
                        </strong>
                    </div>
                    <div className={style['right']}>
                        <button className={style['setting-btn']} onClick={() => { navigate(URL.CONTACTLESS_SETTING, { state: { user } }) }}>
                            <span className='blind'>앱설정</span>
                        </button>
                        <button className={style['setting-notice']} onClick={() => { navigate(URL.CONTACTLESS_NOTI) }}>
                            <span className='blind'>알림설정</span>
                        </button>
                    </div>
                </div>

                <div className={style['patient-inform']}>
                    <strong>Patient Information</strong>
                    <div className={style['patient-inform-box']}>
                        <ul>
                            <li>
                                <p>Name</p>
                                <span>{user?.usrNm}</span>
                            </li>
                            <li>
                                <p>Date of Birth</p>
                                <span>{user !== null ? formatDate(user?.brthdy) : ""} (Age {user !== null ? getAgeByBirthday(user?.brthdy) : ""})</span>
                            </li>
                            <li>
                                <p>Patient Number</p>
                                <span>{user?.usrHsptlMap[0]?.cnncPatntNo}</span>
                            </li>
                        </ul>
                    </div>
                </div>

                <div className={style['consultation-inform']}>

                    <strong>Consultation Information</strong>

                    <div className={style['tabs-box']}>
                        <Tabs value={tabValue} onChange={handleOnTabChange} orientation="vertical">

                            <div className="top-tab-head" data-type="c">
                                <TabsList>
                                    <Tab value={"before"}>Upcoming</Tab>
                                    <Tab value={"after"}>Past Consultations</Tab>
                                </TabsList>
                            </div>


                            <TabPanel value={"before"} className={style['tab-panel-box']}>

                                <div className={style['infrom-list-box']}>

                                    {loading && <ConsultationSkeleton />}

                                    {!loading && telemedicineList.length === 0 && (
                                        <div className={style['no-data-box']}>
                                            <div className='animation-1'>
                                                <div className={style['img-box']}>
                                                    <object data={noDataImg} aria-hidden='true' />
                                                </div>
                                                <p>I don't have a Consultation.</p>
                                            </div>
                                        </div>
                                    )}


                                    {!loading && telemedicineList.length > 0 &&

                                        telemedicineList.map((item, idx) => (

                                            <div key={`infrom-before-` + idx} className={style['infrom-box']}
                                                onClick={item.telmdcSeCd === "A" ? () => navigate(URL.CONTACTLESS_DETAIL, { state: item }) : undefined} >
                                                <div className={style['top']}>
                                                    <div className={style['left']}>
                                                        <span>{item.telmdcSeNm}</span>
                                                        <strong>{item.telmdcEntryDt}</strong>
                                                        <div className={style['txt-box']}>
                                                            <p className={style['hosp-name']}>{item.cnncHsptlNm}</p>
                                                            <span>{item.cnncDeptNm}</span>
                                                            <strong>{item.prcsMdcstffNm}</strong>
                                                        </div>
                                                    </div>
                                                    <div className={style['right']}>
                                                        <span className={style['status-box']}>
                                                            D-{getCalculateDDay(item.telmdcBgngDt.substring(0, 8)) !== 0 ?
                                                                getCalculateDDay(item.telmdcBgngDt.substring(0, 8))
                                                                : "Day"
                                                            }
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className={style['bottom']}>
                                                    {/* 테스트 완료 후, 버튼은 true : false 설정 */}
                                                    <button
                                                        className='btn'
                                                        data-btn-size="lg"
                                                        data-btn-type="fill-main"
                                                        data-btn-width='full'
                                                        onClick={(e) => {
                                                            e.stopPropagation();

                                                            if (!isJoinAvailableKST(item.telmdcBgngDt)) {
                                                                setToastMessage('Entry is available 15 minutes \n prior to the event.');
                                                                setToast(true);
                                                            } else {
                                                                handleOnJoinMeeting(item.telmdcUrl);
                                                            }
                                                        }}
                                                        data-disabled={!isJoinAvailableKST(item.telmdcBgngDt)}
                                                    >
                                                        <span>Join {item.telmdcSeNm}</span>
                                                    </button>
                                                </div>
                                            </div>

                                        ))
                                    }
                                    {/* sample backup ... 정상오픈 전에는 주석부분 다 삭제 처리 할것! 지저분함
                                <div className={style['infrom-box']} onClick={()=> navigate(URL.CONTACTLESS_DETAIL)} >
                                    <div className={style['top']}>
                                        <div className={style['left']}>
                                            <span>Pre-consultationg</span>
                                            <strong>25.03.03 (Mon) 03:11 PM</strong>
                                            <div className={style['txt-box']}>
                                                <p className={style['hosp-name']}>Seoul St. Mary's Hospital</p>
                                                <span>International Medical Center</span>
                                                <strong>Kim Sangdam</strong>
                                            </div>
                                        </div>
                                        <div className={style['right']}>
                                            <span className={style['status-box']}>D-day</span>
                                        </div>
                                    </div>
                                    <div className={style['bottom']}>
                                        <button className='btn' data-btn-size="lg" data-btn-type="fill-main" data-btn-width='full'>
                                            <span>Join Test Meeting</span>
                                        </button>
                                    </div>
                                </div>

                                <div className={style['infrom-box']} onClick={()=> navigate(URL.CONTACTLESS_DETAIL)} >
                                    <div className={style['top']}>
                                        <div className={style['left']}>
                                            <span>Treatment</span>
                                            <strong>25.03.03 (Mon) 03:11 PM</strong>
                                            <div className={style['txt-box']}>
                                                <p className={style['hosp-name']}>Seoul St. Mary's Hospital</p>
                                                <span>International Medical Center</span>
                                                <strong>Kim Sangdam</strong>
                                            </div>
                                        </div>
                                        <div className={style['right']}>
                                            <span className={style['status-box']}>D-day</span>
                                        </div>
                                    </div>
                                    <div className={style['bottom']}>
                                        <button className='btn' data-btn-size="lg" data-btn-type="fill-main" data-btn-width='full'>
                                            <span>Join Test Meeting</span>
                                        </button>
                                    </div>
                                </div>
                                */}

                                </div>
                            </TabPanel>


                            <TabPanel value={"after"} className={style['tab-panel-box']}>

                                <div className={style['infrom-list-box']}>

                                    <div className={style['infrom-list-box']}>

                                        {loading && <ConsultationSkeleton />}

                                        {!loading && telemedicineList.length === 0 && (
                                            <div className={style['no-data-box']}>
                                                <div className='animation-1'>
                                                    <div className={style['img-box']}>
                                                        <object data={noDataImg} aria-hidden='true' />
                                                    </div>
                                                    <p>I don't have a Consultation.</p>
                                                </div>
                                            </div>
                                        )}

                                        {!loading && telemedicineList.length > 0 &&

                                            telemedicineList.map((item, idx) => (

                                                <div key={`infrom-after-` + idx} className={style['infrom-box']}>
                                                    <div className={style['top']}>
                                                        <div className={style['left']}>
                                                            <span>{item.telmdcSeNm}</span>
                                                            <strong>{item.telmdcEntryDt}</strong>
                                                            <div className={style['txt-box']}>
                                                                <p className={style['hosp-name']}>{item.cnncHsptlNm}</p>
                                                                <span>{item.cnncDeptNm}</span>
                                                                <strong>{item.prcsMdcstffNm}</strong>
                                                            </div>
                                                        </div>
                                                        <div className={style['right']}>{/* 쿼리데이터에서 필터된 것이니 그대로 end 로 써도 됨. */}
                                                            <span className={style['status-box']}>END</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))
                                        }
                                    </div>
                                </div>
                            </TabPanel>

                        </Tabs>
                    </div>
                    {/* 토스트 */}
                    {toast && (
                        <ToastPopup
                            setToast={setToast}
                            position='center'
                            message={toastMessage}
                        />
                    )}

                </div>
            </div>


        </>
    );
};

export default ContactlessMain;
